<script setup>
import { computed, onBeforeMount, nextTick, onMounted, onUnmounted, watch, reactive, ref } from 'vue'
import { fetchData, fetchAllData, fetchImage } from '../loadData';
import languagePropmptTranslations from '../modalUpdateLanguage-translations.js';

/* COMPONENTS */
import GlobalNav from './GlobalNav.vue';
import GlobalNavDrawer from './GlobalNavDrawer.vue';
import ClearFilters from './ClearFilters.vue';
/* CURRICULUM */
import ArticleOverview from './ArticleOverview.vue';
import ArticleFrame from "./ArticleFrame.vue";
import ArticleReactions from './ArticleReactions.vue';
import Curriculum from './Curriculum.vue';
import OverviewControlBar from './OverviewControlBar.vue';
import ShareSheet from './ShareSheet.vue';
import AccountInfo from './AccountInfo.vue';
/* MODALS */
import ModalAuthLogin from './ModalAuthLogin.vue';
import ModalServerReconnect from './ModalServerReconnect.vue';
import ModalRegistrationPrompt from './ModalRegistrationPrompt.vue';
import ModalUpdateLanguage from './ModalUpdateLanguage.vue';

/* UTILITIES */
import { appStorage } from '@shared/utils/storage.js';
import { updateArticleStatus } from "../utils/updateArticleStatus.js";
import { updateStorage } from "../utils/updateStorage.js";
import { normalizeTrainingData } from "../utils/normalizeTrainingData.js";

/* PAGES */
// import Quotes from './Quotes.vue';
// import Messages from './Messages.vue';
// import Notes from './Notes.vue';
import SupportPage from './SupportPage.vue';
import GamesPage from './GamesPage.vue';
import GamesCurriculum from './GamesCurriculum.vue';

const contentFiles = ref({}); // receiver for article-data
const gamesData = ref([]);
const selectedGameId = ref('g0');
const selectedGame = computed(() => gamesData.value.flatMap(category => category.articles || []).find(game => game.id === selectedGameId.value));
let articlesViewed = 0;
const alreadyPromptedToRegister = ref(false);
const currentArticle = reactive({ title:'', summary:'', id:'' });
const activeContentType = ref('article');
const contentLanguage = ref('en'); // worth noting that en and en-US come through as distinct langs in chrome
const currentPage = ref("curriculum");
const curriculum = reactive([]);
const sidebarRef = ref(null);
const declinedRegistrationOnce = ref(false);
const isArticleInverted = ref(false);
const isDeleteAccountModalOpen = ref(false);
const isDisliked = ref(false);
const isSaved = ref(false);
const isGlobalNavOpen = ref(false);
const isLanguageModalOpen = ref(false);
const isLiked = ref(false);
const isLoginModalOpen = ref(false);
const isServerReconnectModalOpen = ref(false);
const isRead = ref(false);
const isRegistrationPromptOpen = ref(false);
const isShareSheetOpen = ref(false);
const isSidebarVisible = ref(true);
const isUpdatePasswordModalOpen = ref(false);
const searchParams = ref(new URL(window.location).searchParams);
const subPage = ref('');
const user = reactive({});
const wasRedirected = ref(false);
const windowWidth = ref(window.innerWidth);
let restoringHistory = false;
const activeArticle = computed(() => activeContentType.value === 'article' ? currentArticle : { id: '' });
const hasSavedItems = computed(() => {
	const training = user.training || {};
	const hasSavedArticle = curriculum.some(section => section.articles.some(article => training[article.id]?.saved));
	const hasSavedGame = gamesData.value.some(category => (category.articles || []).some(game => !game.overview && training[game.id]?.saved));
	return hasSavedArticle || hasSavedGame;
});
const savedCount = computed(() => {
	const training = user.training || {};
	const articleCount = curriculum.reduce((count, section) => count + section.articles.filter(article => training[article.id]?.saved).length, 0);
	const gameCount = gamesData.value.reduce((count, category) => count + (category.articles || []).filter(game => !game.overview && training[game.id]?.saved).length, 0);
	return articleCount + gameCount;
});

// COMPUTED PROPS
const showIframe = computed(() => {
	return (currentArticle.archive === false) && (currentArticle.embed === true || currentArticle.privacy) && (windowWidth.value >= 1024) && (currentArticle.section !== 's0');
});

const autoHideNav = computed(() => {
	return windowWidth.value < 1024 || ['curriculum', 'saved', 'games'].includes(currentPage.value)
});

const preferredLanguage = computed(() => {
	const storedUser = appStorage.get('user');
	return user?.profile?.preferredLanguage ?? storedUser?.profile?.preferredLanguage ?? null;
});

const localizedUrl = computed(() => {
	if (!currentArticle?.url) { return ''; }

	const params = new URLSearchParams(window.location.search);
	params.delete('article');
	const cleanedSearchString = params.toString();

	const articleUrl = currentArticle.privacy ?? currentArticle.url;

	if (searchParams.value.get('_x_tr_tl')) { // viewing site through google translate
		let protocol = articleUrl.slice(0, articleUrl.indexOf('://')+3);
		let href = articleUrl.slice((articleUrl.indexOf('://')+3));
		let domain = href.slice(0, href.indexOf('/')).split('.').join('-');
		let path = href.slice(href.indexOf('/')+1);


		console.log(`${protocol} \n${domain} \n.translate.goog/ \n${path} \n? \n${cleanedSearchString}`)

		return `${protocol}${domain}.translate.goog/${path}?${cleanedSearchString}`;

	} else {
		return articleUrl;
	}
});

// LIFECYCLE HOOKS
onBeforeMount(async () => {
	await loadData();
	setUpUser();
})

const ARTICLE_DATA_VERSION = 14;
function buildCurriculum(articleData) {
	curriculum.splice(0, curriculum.length, ...Object.entries(articleData || {}).map(([tag, section]) => ({
		label: section.label,
		tag,
		id: section.id,
		articles: Object.entries(section.articles || {}).map(([id, article]) => ({ ...article, id })),
	})));
}

async function loadData() {
	gamesData.value = await fetchData('games.json') || [];
	const cached = appStorage.get('articleData');
	const cachedVersion = appStorage.get('articleDataVersion');

	if (cached && cachedVersion === ARTICLE_DATA_VERSION) {
		contentFiles.value = cached;
		buildCurriculum(cached);
		return;
	}

	console.log('data is outdated / missing; downloading from server');
	// Fetch new data if not cached or outdated
	const data = await fetchAllData();  // described in /vue_study/src/loadData.js
	contentFiles.value = data;
	buildCurriculum(data);
	appStorage.set('articleData', data);
	appStorage.set('articleDataVersion', ARTICLE_DATA_VERSION);
}

onMounted(async () => {
	await loadData();
	initialize();
	window.addEventListener('resize', updateWindowWidth);
	window.addEventListener('resize', autoToggleNav);
	window.addEventListener('popstate', restoreNavigationFromHistory);
	window.addEventListener('keydown', dismissShareSheetOnEscape);
})

onUnmounted(() => {
	window.removeEventListener('resize', updateWindowWidth);
	window.removeEventListener('resize', autoToggleNav);
	window.removeEventListener('popstate', restoreNavigationFromHistory);
	window.removeEventListener('keydown', dismissShareSheetOnEscape);
})

function dismissShareSheetOnEscape(event) {
	if (event.key === 'Escape' && window.innerWidth < 1024 && isShareSheetOpen.value) {
		toggleModals();
	}
}

/* LANGUAGE OPTIONS */
function setUpLanguage() {
	// const browserLanguage = navigator.language.split('-')[0];

	// already translated. get language from search params
	// if (window.location.host === 'brownjuice-co.translate.goog') {
	// 	contentLanguage.value = searchParams.value.get('_x_tr_tl');
	// 	return;
	// }
	// short-circuit if user has specified that they prefer english
	// if (preferredLanguage?.value === 'en' || browserLanguage === 'en' ) {
	// 	contentLanguage.value = 'en';
	// 	return;
	// }
	// update content if user prefers non-english language
	// if (!!preferredLanguage.value && preferredLanguage.value !== 'en') {
	// 	updateContentLanguage(preferredLanguage.value);
	// 	return;
	// }

	// if (supportedLanguages.includes(browserLanguage)) {
	// 	// prompt to update language for non-english users
	// 	window.setTimeout(() => {
	// 		isLanguageModalOpen.value = true;
	// 	}, 3000);
	// } else {
	// 	// automatically translate for edge-case languages (with no translation in our prompt)
	// 	updateContentLanguage(browserLanguage);
	// }
}
function updateContentLanguage(targetLanguage) {
	isLanguageModalOpen.value = false;
	// if (targetLanguage === contentLanguage.value) { return; }

	// contentLanguage.value = targetLanguage;

	// window.location = `https://brownjuice-co.translate.goog/study/?article=${currentArticle.id}&_x_tr_sl=en&_x_tr_tl=${targetLanguage}&_x_tr_hl=${targetLanguage}&_x_tr_pto=wapp`; // &_x_tr_pto=wapp <-- not needed?

	// displayAlert('', 'Create an account to save your preferences', 'toast')
};

// METHODS
async function initialize() {
	// set up user object -- this should be unnecessary since it was called in onBeforeMount
	if (!Object.keys(user).length) { setUpUser(); }

	// set status on curriculum-list
	if (user?.training && Object.keys(user.training).length) {
		Object.keys(user?.training).forEach(function(record) {
			var listItem = document.querySelector(`[data-id='${record}']`);
			if (listItem && user.training[record]?.saved) {
				listItem.classList.add('saved');
			} else if (listItem && user.training[record]?.read) {
				listItem.classList.add('read');
			}
		});
	}

	// TODO: restore collapsed sections

	const requestedPage = searchParams.value.get('page');
	const requestedGame = searchParams.value.get('games') || searchParams.value.get('game');
	const requestedArticle = searchParams.value.get('article');

	restoringHistory = true;
	if (requestedPage === 'saved') {
		if (requestedGame !== null || requestedArticle !== null) {
			await navigateToSavedItem(requestedArticle, requestedGame);
		} else {
			await openDefaultSavedItem();
		}
	} else if (requestedGame !== null || requestedPage === 'games') {
		await updatePage('games');
		if (requestedGame !== null) selectGame(requestedGame);
	} else if (requestedPage) {
		// load the requested top-level page
		document.querySelector(`[name='${requestedPage}']`)?.click();
	} else if (requestedArticle !== null) {
		// load the requested curriculum article
		await loadData();
		const articleID = requestedArticle || 'a0';
		deepLinkCurriculum(articleID);
	} else if (window.location.search) {
		// unknown search params -- start from the welcome message
		deepLinkCurriculum('a0');
	} else if (user.trainingLastArticle) {
		await loadData();
		deepLinkCurriculum(user.trainingLastArticle);
	} else { // no params, no data -- new user
		deepLinkCurriculum('a0');
	}
	restoringHistory = false;


	declinedRegistrationOnce.value = !!appStorage.get('registrationPromptDeclinedOnce');

	// setUpLanguage();
	autoToggleNav();
	autoToggleSidebar();
}

async function navigateToSavedItem(articleId, gameId) {
	if (gameId !== null && gameId !== undefined) {
		const game = gamesData.value.flatMap(category => category.articles || []).find(item => item.id === gameId);
		const isSaved = !!user.training?.[gameId]?.saved;
		await updatePage(isSaved ? 'saved' : 'games', undefined, isSaved ? gameId : undefined);
		selectGame(game?.id || gameId);
		return;
	}

	if (articleId !== null && articleId !== undefined) {
		const entry = Object.entries(contentFiles.value).find(([, section]) => section.articles?.[articleId]);
		const destination = user.training?.[articleId]?.saved ? 'saved' : 'curriculum';
		if (entry) {
			await updatePage(destination, undefined, articleId);
			if (destination === 'saved') await openArticle(articleId, entry[0], true, true);
		}
	}
}

function getDefaultSavedItem() {
	const training = user.training || {};
	const savedArticles = curriculum
		.flatMap(section => section.articles.map(article => ({ article, section })))
		.filter(({ article }) => training[article.id]?.saved);
	const lastViewedSavedArticle = savedArticles.find(({ article }) => article.id === user.trainingLastArticle);
	const firstSavedArticle = lastViewedSavedArticle || savedArticles[0];
	if (firstSavedArticle) return { type: 'article', ...firstSavedArticle };

	const firstSavedGame = gamesData.value
		.flatMap(category => category.articles || [])
		.find(game => !game.overview && training[game.id]?.saved);
	if (firstSavedGame) return { type: 'game', game: firstSavedGame };
	return null;
}


async function openDefaultSavedItem() {
	await updatePage('saved');
}

async function activateDefaultSavedItem() {
	const item = getDefaultSavedItem();
	if (item?.type === 'article') {
		await openArticle(item.article.id, item.section.tag, true, true);
	} else if (item?.type === 'game') {
		selectGame(item.game.id);
	} else {
		await updatePage('curriculum', undefined, 'a0');
		searchParams.value = new URLSearchParams('article=a0');
		history.replaceState(history.state, '', `${window.location.pathname}?article=a0`);
	}
}

function updateWindowWidth() {
	const previousWidth = windowWidth.value;
	windowWidth.value = window.innerWidth;
	const enteredTabletFromMobile = previousWidth < 768 && windowWidth.value >= 768 && windowWidth.value < 1024;
	const leftTablet = previousWidth >= 768 && previousWidth < 1024 && (windowWidth.value < 768 || windowWidth.value >= 1024);
	const enteredDesktop = windowWidth.value >= 1024;
	if ((enteredTabletFromMobile || leftTablet || enteredDesktop) && ['curriculum', 'saved', 'games'].includes(currentPage.value)) {
		isSidebarVisible.value = true;
	}
	if (isShareSheetOpen.value) toggleModals();
}
function autoToggleNav() {
	isGlobalNavOpen.value = !autoHideNav.value;
}
function autoToggleSidebar() {
	// Keep the quick-access sidebar available across page changes, except on
	// Support and Account where it is not part of the tablet layout.
	isSidebarVisible.value = windowWidth.value >= 1024 || !['support', 'account'].includes(currentPage.value);
}

function selectGame(gameId) {
	const savedGameId = appStorage.get('trainingLastGame');
	const selectedId = gameId || savedGameId || 'g0';
	const game = gamesData.value.flatMap(category => (category.articles || []).map(item => ({ ...item, category: category.label, categoryId: category.id }))).find(item => item.id === selectedId)
		|| gamesData.value.flatMap(category => category.articles || []).find(item => item.id === 'g0');
	if (!game) return;
	selectedGameId.value = game.id;
	activeContentType.value = 'game';
	appStorage.set('trainingLastGame', game.id);
	const inSaved = currentPage.value === 'saved';
	if (currentPage.value !== 'games' && !inSaved) currentPage.value = 'games';
	subPage.value = '';
	searchParams.value.delete('article');
	if (inSaved) searchParams.value.set('page', 'saved');
	else searchParams.value.delete('page');
	searchParams.value.delete('game');
	searchParams.value.set('games', game.id);
	history.replaceState(history.state, '', `${window.location.pathname}?${searchParams.value.toString()}`);
	if (windowWidth.value < 768 && isSidebarVisible.value) toggleSidebar('overview');
	if (isGlobalNavOpen.value) isGlobalNavOpen.value = false;
	requestAnimationFrame(() => document.getElementById('games-overview')?.scrollTo({ top: 0, left: 0, behavior: 'instant' }));
}

async function restoreNavigationFromHistory() {
	restoringHistory = true;
	searchParams.value = new URL(window.location).searchParams;
	const page = searchParams.value.get('page');
	const gameId = searchParams.value.get('games') || searchParams.value.get('game');
	const articleId = searchParams.value.get('article');
	if (page === 'saved' && (gameId || articleId)) {
		await navigateToSavedItem(articleId, gameId);
	} else if (page === 'saved') {
		await openDefaultSavedItem();
	} else if (gameId || page === 'games') {
		await updatePage('games');
		selectGame(gameId || 'g0');
	} else if (page && page !== 'curriculum' && page !== 'saved') {
		await updatePage(page);
	} else {
		currentPage.value = page === 'saved' ? 'saved' : 'curriculum';
		subPage.value = '';
		await nextTick();
		if (articleId) {
			const entry = Object.entries(contentFiles.value).find(([, section]) => section.articles?.[articleId]);
			if (entry) await openArticle(articleId, entry[0], true, true);
		}
	}
	restoringHistory = false;
}

async function openArticleSummary(article) {
	currentPage.value = 'curriculum';
	activeContentType.value = 'article';
	subPage.value = '';
	isSidebarVisible.value = windowWidth.value >= 768;
	autoToggleNav();
	searchParams.value.delete('games');
	searchParams.value.delete('game');
	searchParams.value.delete('page');
	searchParams.value.set('article', article.id);
	history.pushState({}, '', `${window.location.pathname}?${searchParams.value.toString()}`);
	await nextTick();
	await openArticle(article.id, article.tag, true, true);
}

function deepLinkCurriculum(articleID, showSummary = true) {
	const link = document.body.querySelector(`[data-id='${articleID}']`);

	// scroll link into view
	if (link) {
		window.setTimeout(()=>{
			link.classList.add('active');
			sidebarRef.value?.scrollToActive()

			const curriculumList = document.getElementById('curriculum');
			const el = curriculumList.querySelector('.active');
			if (showSummary) {
				el.click();
			} else {
				let section = el.parentElement.parentElement
				openArticle(el.getAttribute('data-id'), section.getAttribute('data-section'), false, true);
			}
		}, 10);
	}
}

async function toggleNav(initialize) {
	// hide any open modals if open
	if (!initialize) {toggleModals();}

	if (isGlobalNavOpen.value) {
		isGlobalNavOpen.value = false;
		return;
	}
	// opening
	isGlobalNavOpen.value = true;
};

function promptToRegister() {
	const articlesWithReactions = Object.values(user.training).filter(obj =>
		Object.values(obj).some(value => value === true)
	).length;

	const registrationPromptDeclined = appStorage.get('registrationPromptDeclined');
	if (articlesWithReactions < 3 || user._id || alreadyPromptedToRegister.value || registrationPromptDeclined) {return;}

	alreadyPromptedToRegister.value = true;
	isRegistrationPromptOpen.value = true;
}

function setUpUser() { // called beforeMount
	let tempUserData = appStorage.get('user') ?? { profile: {}, training: {} };
	tempUserData.training = normalizeTrainingData(tempUserData.training);
	Object.assign(user, tempUserData);
	appStorage.set('user', tempUserData);

	const activeSessionOnServer = document.body.getAttribute('data-auth-status') === 'auth';
	// update nav if user is registered
	if (activeSessionOnServer) {
		if (user?._id) {
			// user is signed in both locally and on server
			document.getElementById('global-nav').classList.add('auth');
		} else {
			// user is signed in on server but not locally -- fetch user data from server
			fetchData('../../../api/users/me').then((data) => {
				login(data);
			}).catch((err) => {
				console.error('Error fetching user data from server:', err);
				logout();
			});
		}
	} else if (user?._id) {
		// Important!!! this is to force the modal to launch for development purposes, TODO: delete later
		// toggleModals('server-reconnect');
		// user is signed in locally but not on server -- clear local data
		toggleModals('server-reconnect');
	}
}

function isEmptyArticleRecord(record) {
	return !Object.values(record).some(v => v);
}

function isNewUser(created) {
	// check if the user was created in the last 5 minutes
	const FIVE_MINUTES = 1000 * 60 * 5;
	const createdTime = new Date(created).getTime();
	return Date.now() - createdTime < FIVE_MINUTES;
}

function updateUserFromServer(serverUser) {
	serverUser.training = normalizeTrainingData(serverUser.training);
	// Case 1: Signed in previously, and server is up-to-date or ahead of local user
	if (user._id && serverUser.training && Object.keys(serverUser.training).length >= Object.keys(user.training).length) {
		// overwrite local data with server data if the local has fewer records than the server
		Object.assign(user, serverUser);
		updateStorage(user);
	}

	// Case 2: New account — prioritize localStorage training data
	if (serverUser.created && isNewUser(serverUser.created)) {
		const updatedUser = {
			...serverUser,
			training: user.training || {},
		};

		if (user) { Object.assign(user, updatedUser); }
		else if (user2) { Object.assign(user2, updatedUser); }
		updateStorage(user);
	}

	// Case 3: Existing user, new device — merge training records
	const mergedUser = serverUser;
	if (!mergedUser.training) mergedUser.training = {};

	const serverTraining = serverUser.training || {};
	const localTraining = user.training || {};

	const articleKeys = new Set([
		...Object.keys(localTraining),
		...Object.keys(serverTraining),
	]);

	for (const key of articleKeys) {
		const localRecord = localTraining[key] || {};
		const serverRecord = serverTraining[key] || {};

		const mergedRecord = {
			like: localRecord.like || serverRecord.like || false,
			dislike: localRecord.dislike || serverRecord.dislike || false,
			saved: localRecord.saved || serverRecord.saved || false,
			read: localRecord.read || serverRecord.read || false,
		};

		if (!isEmptyArticleRecord(mergedRecord)) {
			mergedUser.training[key] = mergedRecord;
		}
	}

	// Preserve trainingLastArticle from current session
	mergedUser.trainingLastArticle = localTraining.trainingLastArticle;

	Object.assign(user, mergedUser);
	updateStorage(user, true);
	return;
}

function login(data) {
	document.getElementById('global-nav').classList.add('auth');

	updateUserFromServer(data);

	if (currentPage.value === 'curriculum') {
		refreshCurrentArticle();
	}
}

function logout() {
	tagEvent({where: 'authentication', what: 'sign_out'});

	// clear caches
	appStorage.remove('user');
	appStorage.remove('collapsedSections');
	document.body.setAttribute('data-auth-status', 'signed-out');
	Object.keys(user).forEach(key => delete user[key]);
	setUpUser();

	document.getElementById('global-nav').classList.remove('auth');

	if (currentPage.value === 'curriculum') {
		refreshCurrentArticle();
	} else {
		updatePage('curriculum');
	}

	displayAlert('', 'Signed Out', 'toast');
}

function passwordUpdated() {
	tagEvent({where: 'authentication', what: 'password_updated'});
	toggleModals();
	displayAlert('Success', 'Password Updated', 'center');
}

async function refreshCurrentArticle() {
	const foundEntry = Object.entries(contentFiles).find(([key, section]) => section.id === currentArticle.section);

	if (foundEntry) {
		const [key, section] = foundEntry;
		section.tag = key;
		await nextTick();
		openArticle(currentArticle.id, section, true);
	}
}

function handleArticleAction(action, desiredState, articleID = currentArticle.id, event) {
  if (windowWidth.value < 768 && event) event.stopPropagation();

  // special-case: only likes need the registration prompt
  if (action === "like") {
    promptToRegister();
  }

  updateArticleStatus({
    articleID,
    action,
    desiredState,
    user,
    refs: { currentArticle, isRead, isSaved, isLiked, isDisliked },
    tagEvent,
    updateStorage,
  });
}

function displayAlert(heading, messages, alertType, buttonLabels) {
	// TODO: convert params to an object
	var app = document.getElementById('app');
	var div = document.createElement('div');
	var isToast = alertType === 'toast';
	var messageEl;
	var button;

	// add heading (if any)
	if (heading) {
		var headingEl = document.createElement('h1');
		headingEl.appendChild(document.createTextNode(heading));
		div.append(headingEl);
	}

	// add paragraphs (if any)
	if (messages) {
		if (typeof messages == "string") {
			messageEl = document.createElement('p');
			messageEl.innerHTML = messages;
			div.append(messageEl);

		} else if (messages && Array.isArray(messages)) {
			messages.forEach(function(message) {
				messageEl = document.createElement('p');
				messageEl.innerHTML = message;
				div.append(messageEl);
			});
		}
	}

	// set attributes (if any)
	div.setAttribute('id', 'alert-message')
	div.setAttribute('class', ('alert '+(alertType || '')))

	// add buttons (if any)
	if (buttonLabels) {
		if (typeof buttonLabels == "string") {
			button = document.createElement('a');
			button.setAttribute('class', 'btn wide primary');
			button.appendChild(document.createTextNode(buttonLabels));
			div.append(button);

		} else if (Array.isArray(buttonLabels)) {
			buttonLabels.forEach(function(label, i) {
				button = document.createElement('a');
				button.setAttribute('class', 'btn wide '+(i === 0 ? 'primary' : 'secondary'))
				button.appendChild(document.createTextNode(label));
				div.append(button);
			});
		}
	}

	// attach alert
	app.prepend(div);

	// configure to dismiss
	if (isToast) {
		// animate in
		window.setTimeout(function() {
			div.classList.add('show');
		}, 5);

		// animate out
		window.setTimeout(function() {
			div.classList.remove('show');

			// remove from dom
			window.setTimeout(function() {
				div.remove();
			}, 300);
		}, 2600);

	} else if (!buttonLabels) {
		// automate fade-out
		window.setTimeout(function() {
			div.classList.add('fade-out');

			// remove from dom
			window.setTimeout(function() {
				div.remove();
			}, 300);
		}, 3000);

	} else {
		// add events to button(s)
		const buttons = div.querySelectorAll('a.btn');
		buttons.forEach((btn) => {
			const closeAlert = () => {
				div.classList.add('fade-out');
				setTimeout(() => {
					div.remove();
				}, 300);

				// Clean up the event listener after it's clicked
				btn.removeEventListener('click', closeAlert);
			};

			btn.addEventListener('click', closeAlert);
		});
	}
}

async function redirectToCurriculum() {
	const lastArticle = user.trainingLastArticle;
	const articleId = lastArticle && contentFiles.value
		&& Object.values(contentFiles.value).some(section => section.articles?.[lastArticle])
		? lastArticle
		: 'a0';

	currentPage.value = 'curriculum';
	activeContentType.value = 'article';
	subPage.value = '';
	searchParams.value.delete('games');
	searchParams.value.delete('game');
	searchParams.value.delete('page');
	searchParams.value.set('article', articleId);
	history.replaceState(history.state, '', `${window.location.pathname}?${searchParams.value.toString()}`);
	await nextTick();
	deepLinkCurriculum(articleId);
}

async function updatePage(pageName, newSubPage, requestedItemId) {
	if (pageName == currentPage.value && subPage.value == newSubPage && !requestedItemId && pageName !== 'saved') { return; }

	// sign-in/sign-out handled in AuthLoginModal.js. Direct links may target
	// pages that are intentionally omitted from the navigation (such as Games).
	const pageLink = document.querySelector(`[name='${newSubPage || pageName }']`);
	if (!user._id && pageLink?.classList.contains('authReqd')) {
		// user is not signed in
		console.log('redirected from authReqd');
		await redirectToCurriculum();
		return;
	}

	switch(pageName) { // short-circuit for non-page links
		case 'authenticate':
			// always close on mobile or curriculum
			autoToggleNav();
			toggleModals("auth-login");
			return;

		case 'sign-out':
			// always close on mobile or curriculum
			autoToggleNav();
			logout();
			return;

		default:
			// continue to update page
	}

	// update page
	currentPage.value = pageName;
	subPage.value = newSubPage || '';
	var newUrl;
		switch(pageName) {
		case 'curriculum':
		case 'saved':
			activeContentType.value = 'article';
			searchParams.value.delete('article');
			searchParams.value.delete('games');
			searchParams.value.delete('game');
			searchParams.value.delete('page');
			if (pageName === 'saved') searchParams.value.set('page', 'saved');
			else searchParams.value.set('article', requestedItemId || (user?.trainingLastArticle != undefined ? `${user.trainingLastArticle}` : 'a0'));
			newUrl = `${window.location.pathname}?${searchParams.value.toString()}`;
			if (restoringHistory) history.replaceState(history.state, '', newUrl);
			else history.pushState({}, '', newUrl);

			await nextTick();
			autoToggleNav();
			isSidebarVisible.value = true;

			// restore curriculum scroll-position
			if (pageName === 'curriculum') {
				const articleToOpen = requestedItemId || user?.trainingLastArticle || 'a0';
				const automaticallyOpenArticle = windowWidth.value < 768 ? false : true;
				deepLinkCurriculum(articleToOpen, automaticallyOpenArticle);
			} else if (!requestedItemId) {
				await activateDefaultSavedItem();
			}
			break;

		case 'games':
			activeContentType.value = 'game';
			searchParams.value.delete('article');
			searchParams.value.delete('page');
			searchParams.value.set('games', searchParams.value.get('games') || searchParams.value.get('game') || appStorage.get('trainingLastGame') || 'g0');
			newUrl = `${window.location.pathname}?${searchParams.value.toString()}`;
			if (restoringHistory) history.replaceState(history.state, '', newUrl);
			else history.pushState({}, '', newUrl);
			autoToggleNav();
			await nextTick();
			selectGame(searchParams.value.get('games') || searchParams.value.get('game') || appStorage.get('trainingLastGame') || 'g0');
			// Entering the Games category starts on its list on mobile, like Curriculum.
			if (windowWidth.value < 768) isSidebarVisible.value = true;
			else autoToggleSidebar();
			break;

		case 'support':
		case 'account':
			autoToggleNav();
			const contentContainer = document.getElementById('sidebar').nextElementSibling;
			contentContainer?.scrollTo({top: 0, left: 0, behavior: 'instant'});

		default:
			// update search parameters to reflect the current page
			searchParams.value.delete('article');
			searchParams.value.delete('games');
			searchParams.value.delete('game');
			searchParams.value.delete('page');
			searchParams.value.set('page', newSubPage || pageName);
			newUrl = `${window.location.pathname}?${searchParams.value.toString()}`;
			if (restoringHistory) history.replaceState(history.state, '', newUrl);
			else history.pushState({}, '', newUrl);

			autoToggleSidebar();
			isGlobalNavOpen.value = !autoHideNav.value;
		break;
	}

	await nextTick();
	let links = document.querySelectorAll('#global-nav-drawer a');
	let linksArr = [...links];
	let selectedlink = document.querySelector(`[name='${subPage.value || currentPage.value }']`);

	// clear 'active'
	linksArr.forEach((link) => {
		link.classList.remove('active');
	});

	// set 'active'
	selectedlink?.classList.add('active');
}

async function openArticle(articleID = 'a0', section = 'serviceMessages', force, showList) {
	activeContentType.value = 'article';
	// ensure data is loaded
	if (Object.keys(contentFiles.value).length === 0) { await loadData(); }

	// handle passing in strings instead of section objects
	if (typeof section === 'string') {
		let sectionLabel = section;
		section = contentFiles.value[section];
		section.tag = sectionLabel;
	}

	// populate section 2 article overview
	if (currentArticle.id === articleID && !force) {
		if (windowWidth.value < 768 && !showList && isSidebarVisible.value) toggleSidebar('overview');
		else autoToggleSidebar();
		return;
	}

	// force reset of currentArticle
	Object.keys(currentArticle).forEach(key => delete currentArticle[key]);

	// explicitly unset `true` values
	currentArticle['embed'] = false;
	currentArticle['archive'] = false;

	Object.assign(currentArticle, contentFiles.value[section.tag].articles[articleID], {section: section.id});

	// scroll to the top of the overview
	if (document.getElementById('article-overview')) {
		document.getElementById('article-overview').scrollTo({top: 0, left: 0, behavior: 'instant'});
	}

	// update search parameters to reflect the current article
	searchParams.value.delete('article');
	searchParams.value.delete('games');
	searchParams.value.delete('game');
	if (currentPage.value === 'saved') searchParams.value.set('page', 'saved');
	else searchParams.value.delete('page');
	searchParams.value.set('article', currentArticle.id);
	var newUrl = `${window.location.pathname}?${searchParams.value.toString()}`;
	history.replaceState(history.state, '', newUrl);

	// generate user-data record for article
	user.trainingLastArticle = articleID;
	articlesViewed+=1;

	// set article reactions
	isLiked.value = user.training?.[articleID]?.like ?? false;
	isDisliked.value = user.training?.[articleID]?.dislike ?? false;
	isRead.value = user.training?.[articleID]?.read ?? false;
	isSaved.value = user.training?.[articleID]?.saved ?? false;

	if (windowWidth.value < 768 && !showList) toggleSidebar('overview');
	if (isGlobalNavOpen.value) toggleNav();
	if (articleID != 'a0') tagEvent({ where: 'curriculum', what: 'article_opened' });

	updateStorage(user);
}

function reportArticle() {
	const id = currentArticle.id;
	tagEvent({where: 'article_reactions', what: 'report_issue'});
}

function invertArticle() {
	tagEvent({where: 'article_overview', what: 'invert_article'});
	isArticleInverted.value = !isArticleInverted.value;
}

function toggleSidebar(desiredState = false) {
	isGlobalNavOpen.value = false;
	toggleModals();

	let app = document.getElementById('app');
	app.classList.add('resizing');

	// set the state
	if (desiredState) {
		isSidebarVisible.value = desiredState === 'open' ? true : false;
	} else {
		isSidebarVisible.value = !isSidebarVisible.value;
	}

	// un-swipe curriculumLink
	sidebarRef.value?.setSwipedArticle(null);

	// scroll back to the current article
	if (isSidebarVisible.value) {
		sidebarRef.value?.scrollToActive();
	}

	window.setTimeout(function() {
		app.classList.remove('resizing');
	}, 300);
}

function toggleModals(modal) {
	// default to close everything
	isDeleteAccountModalOpen.value = false;
	isLanguageModalOpen.value = false;
	isLoginModalOpen.value = false;
	isServerReconnectModalOpen.value = false;
	isRegistrationPromptOpen.value = false;
	isShareSheetOpen.value = false;
	isUpdatePasswordModalOpen.value = false;

	const authLogin = document.getElementById('auth-login');
	const serverReconnect = document.getElementById('server-reconnect');
	const deleteAccount = document.getElementById('delete-account');
	const updatePassword = document.getElementById('update-password');
	const shareSheet = document.getElementById('share-sheet');

	authLogin?.close();
	deleteAccount?.close();
	updatePassword?.close();
	shareSheet?.close();

	if (!modal) { return; }

	switch(modal) {
		case 'auth-login':
			isLoginModalOpen.value = true;
			authLogin.showModal();
			break;

		case 'server-reconnect':
			isServerReconnectModalOpen.value = true;
			serverReconnect.showModal();
			break;

		case 'delete-account':
			isDeleteAccountModalOpen.value = true;
			deleteAccount.showModal();
			break;

		case 'language-prompt':
			isLanguageModalOpen.value = true;
			break;

		case 'registration-prompt':
			isRegistrationPromptOpen.value = true;
			break;

		case 'share-sheet':
			isShareSheetOpen.value = true;
			if (window.innerWidth >= 1024) {
				shareSheet?.showModal();
			} else {
				shareSheet?.show();
			}
			break;

		case 'update-password':
			isUpdatePasswordModalOpen.value = true;
			updatePassword.showModal();
			break;
	}
}

function setCopyright() {
	document.querySelector('#copyright-year').innerHTML = new Date().getFullYear();
}

async function tagEvent(data) {/* ANALYTICS */
	if (data.where == 'share_article') { // update share count
		Object.assign(user, { trainingShares: (user.trainingShares ? user.trainingShares+1 : 1) });
		updateStorage(user, true, 'trainingShares');
	}

	/* DATA TEMPLATE
	{
		where: 'page_or_component', // eg 'article_overview'
		status: '', // eg, 'success'
		what: 'eventType', // eg, 'like'
	}
	*/
	const bodyStyle = window.getComputedStyle(document.body).backgroundColor;
	const colorMode = (bodyStyle === "rgb(255, 255, 255)") ? "light" :
										(bodyStyle === "rgb(0, 0, 0)") ? "dark" : bodyStyle;

	const eventData = {
			event_category: data.where,
			event_status: data.status ?? '',
			articles_viewed: user?.articlesViewed || 0,
			value: 0,
			color_mode: colorMode,
			auth: user?._id ? 'true' : 'false',
			current_article: currentArticle?.id ?? 'none'
	};
	// console.log('eventData', eventData, 'data', data);

	let usrs = [/7a4751c483e79/g, /dec56f2f9f967/g, /10059c67c4f26/g];
	if (window.location.host === 'localhost:3000' || window.location.host === 'localhost:5173' || usrs.some((usr) => { return usr.exec(user._id) })) {
		// short circuit
		return;

	} else {
		// Send event to GA
		gtag('event', data.what, eventData);
	}
}
</script>

<template>
	<ModalAuthLogin
		:isLoginModalOpen="isLoginModalOpen"
		:login="login"
		:toggleModals="toggleModals"
		:displayAlert="displayAlert"
		:tagEvent="tagEvent"
	/>
	<ModalServerReconnect
		:isServerReconnectModalOpen="isServerReconnectModalOpen"
		:login="login"
		:logout="logout"
		:toggleModals="toggleModals"
		:displayAlert="displayAlert"
		:tagEvent="tagEvent"
	/>
	<ModalUpdateLanguage
		:isLanguageModalOpen="isLanguageModalOpen"
		:contentLanguage="contentLanguage"
		:updateContentLanguage="updateContentLanguage"
		:languagePropmptTranslations="languagePropmptTranslations"
	/>
	<ModalRegistrationPrompt
		:declinedRegistrationOnce="declinedRegistrationOnce"
		:isRegistrationPromptOpen="isRegistrationPromptOpen"
		:toggleModals="toggleModals"
	/>
	<nav id="sidebar" :class="['column', { collapsed: !isSidebarVisible }]">
		<GlobalNav
			:currentPage="currentPage"
			:currentArticle="currentArticle"
			:selectedGame="selectedGame"
			:isGameActive="activeContentType === 'game'"
			:user="user"
			:windowWidth="windowWidth"
			:isGlobalNavOpen="isGlobalNavOpen"
			:isSidebarVisible="isSidebarVisible"
			:toggleModals="toggleModals"
			:toggleNav="toggleNav"
			:toggleSidebar="toggleSidebar"
			:handleArticleAction="handleArticleAction"
		>
			<template #global-nav-drawer>
				<GlobalNavDrawer
					:currentPage="currentPage"
					:subPage="subPage"
					:user="user"
					:hasSavedItems="hasSavedItems"
					:windowWidth="windowWidth"
					:isGlobalNavOpen="isGlobalNavOpen"
					:updatePage="updatePage"
					:logout="logout"
					:displayAlert="displayAlert"
				/>
			</template>
			<template #clear-filters>
				<ClearFilters
					:updatePage="updatePage"
					:filteredForSaved="currentPage === 'saved'"
				/>
			</template>
		</GlobalNav>
		<Curriculum
			v-if="['curriculum', 'saved'].includes(currentPage)"
			:curriculum="curriculum"
			:currentArticle="activeArticle"
			:currentPage="currentPage"
			:savedCount="savedCount"
			:filteredForSaved="currentPage === 'saved'"
			:user="user"
			:openArticle="openArticle"
			:windowWidth="windowWidth"
			:isSidebarVisible="isSidebarVisible"
			:handleArticleAction="handleArticleAction"
			ref="sidebarRef"
		>
			<template #extra-categories>
				<GamesCurriculum
					v-if="currentPage === 'saved'"
					:games="gamesData"
					:selectedGameId="activeContentType === 'game' ? selectedGameId : ''"
					:selectGame="selectGame"
					:user="user"
					:windowWidth="windowWidth"
					:handleArticleAction="handleArticleAction"
					:savedOnly="true"
				/>
			</template>
		</Curriculum>
		<GamesCurriculum
			v-if="currentPage === 'games'"
			:games="gamesData"
			:selectedGameId="selectedGameId"
			:selectGame="selectGame"
			:user="user"
			:windowWidth="windowWidth"
			:handleArticleAction="handleArticleAction"
		/>
	</nav>

	<template v-if="['curriculum', 'saved'].includes(currentPage) && activeContentType === 'article'">
		<ArticleOverview
			:currentArticle="currentArticle"
			:contentFiles="contentFiles"
			:isArticleInverted="isArticleInverted"
			:showIframe="showIframe"
			:toggleModals="toggleModals"
			:toggleSidebar="toggleSidebar"
			:windowWidth="windowWidth"
		>
			<template #OverviewControlBar>
				<OverviewControlBar
					:currentArticle="currentArticle"
					:localizedUrl="localizedUrl"
					:isRead="isRead"
					:isArticleInverted="isArticleInverted"
					:isLanguageModalOpen="isLanguageModalOpen"
					:windowWidth="windowWidth"
					:invertArticle="invertArticle"
					:reportArticle="reportArticle"
					:tagEvent="tagEvent"
					:toggleModals="toggleModals"
					:handleArticleAction="handleArticleAction"
				/>
			</template>

			<template #ShareSheet>
				<ShareSheet
					:currentArticle="currentArticle"
					:isShareSheetOpen="isShareSheetOpen"
					:toggleModals="toggleModals"
					:tagEvent="tagEvent"
				/>
			</template>

			<template #ArticleReactions>
				<ArticleReactions
					:isShareSheetOpen="isShareSheetOpen"
					:isSidebarVisible="isSidebarVisible"
					:isLiked="isLiked"
					:isDisliked="isDisliked"
					:isSaved="isSaved"
					:currentArticle="currentArticle"
					:localizedUrl="localizedUrl"
					:shareUrl="localizedUrl"
					:tagEvent="tagEvent"
					:windowWidth="windowWidth"
					:toggleModals="toggleModals"
					:toggleSidebar="toggleSidebar"
					:handleArticleAction="handleArticleAction"
				></ArticleReactions>
			</template>
		</ArticleOverview>
		<ArticleFrame
			v-if="windowWidth >= 1024 && (currentArticle?.section !== 's0')"
			:currentArticle="currentArticle"
			:isArticleInverted="isArticleInverted"
			:showIframe="showIframe"
			:markAsRead="markAsRead"
		></ArticleFrame>
	</template>

	<!-- <Quotes v-if="currentPage === 'quotes'" /> -->

	<!-- <Notes v-if="currentPage === 'notes'" /> -->

	<SupportPage
		v-if="currentPage === 'support'"
		id="support"
		:subPage="subPage"
		:displayAlert="displayAlert"
		:user="user"
		:tagEvent="tagEvent"
		:updatePage="updatePage"
		:autoHideNav="autoHideNav"
	/>

	<GamesPage
		v-if="currentPage === 'games' || (currentPage === 'saved' && activeContentType === 'game')"
		:games="gamesData"
		:contentFiles="contentFiles"
		:user="user"
		:handleArticleAction="handleArticleAction"
		:selectedGameId="selectedGameId"
		:selectGame="selectGame"
		:openArticleSummary="openArticleSummary"
		:isShareSheetOpen="isShareSheetOpen"
		:isSidebarVisible="isSidebarVisible"
		:windowWidth="windowWidth"
		:toggleModals="toggleModals"
		:toggleSidebar="toggleSidebar"
		:tagEvent="tagEvent"
	/>

	<template v-if="currentPage === 'account'">
		<main
			v-if="subPage === ''"
			id="account"
			class="column content"
		>
			<AccountInfo
				:curriculum="curriculum"
				:isUpdatePasswordModalOpen="isUpdatePasswordModalOpen"
				:isDeleteAccountModalOpen="isDeleteAccountModalOpen"
				:passwordUpdated="passwordUpdated"
				:tagEvent="tagEvent"
				:toggleModals="toggleModals"
				:user="user"
			/>
		</main>
		<!-- <main
			v-if="subPage === 'messages'"
			class="column content"
		>
			<Messages />
		</main> -->
	</template>
</template>

<style lang="scss" scoped>
</style>
